import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import type { PlaceRecord, EventRecord, CorrectionRequest } from "../lib/types";
import { invokeAdmin, errMsg } from "../lib/adminApi";
import { useAdminQuery } from "../hooks/useAdminQuery";
import { Banner, CopyId, SearchBar, TabButton } from "../components/ui";
import { statusColor } from "../lib/format";

type Tab = "places" | "events" | "corrections";
const TABS: Tab[] = ["places", "events", "corrections"];

const CORRECTION_STATUSES = ["all", "pending", "reviewed", "corrected", "rejected", "ignored"] as const;

export default function RecordsPage() {
  // Tab lives in the URL (?tab=) so back/refresh/deep-links keep it (ADM-12).
  const [params, setParams] = useSearchParams();
  const raw = params.get("tab");
  const tab: Tab = (TABS as string[]).includes(raw ?? "") ? (raw as Tab) : "places";
  const setTab = (t: Tab) => {
    const next = new URLSearchParams(params);
    if (t === "places") next.delete("tab");
    else next.set("tab", t);
    setParams(next, { replace: true });
  };

  return (
    <div>
      <h1 className="text-xl font-semibold">Records</h1>
      <div className="mt-4 flex gap-2 border-b border-zinc-800">
        <TabButton active={tab === "places"} onClick={() => setTab("places")}>
          Places
        </TabButton>
        <TabButton active={tab === "events"} onClick={() => setTab("events")}>
          Events
        </TabButton>
        <TabButton active={tab === "corrections"} onClick={() => setTab("corrections")}>
          Corrections
        </TabButton>
      </div>
      {/* All three tabs stay mounted (just hidden) so switching preserves each
          tab's search text, loaded pages, and scroll offset (ADM-12). */}
      <div className={tab === "places" ? "mt-4" : "hidden"}>
        <PlacesTab />
      </div>
      <div className={tab === "events" ? "mt-4" : "hidden"}>
        <EventsTab />
      </div>
      <div className={tab === "corrections" ? "mt-4" : "hidden"}>
        <CorrectionsTab />
      </div>
    </div>
  );
}

function PlacesTab() {
  const [q, setQ] = useState("");
  const [places, setPlaces] = useState<PlaceRecord[]>([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const search = useCallback(async (newOffset = 0, append = false) => {
    setLoading(true);
    setError(null);
    try {
      const body: Record<string, unknown> = { limit: 50, offset: newOffset };
      if (q.trim()) body["q"] = q.trim();
      const data = await invokeAdmin<{ places: PlaceRecord[] }>("admin_places_list", body);
      setPlaces((prev) => (append ? [...prev, ...data.places] : data.places));
      setOffset(newOffset + data.places.length);
      setHasMore(data.places.length === 50);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [q]);

  useEffect(() => { search(0, false); }, []); // eslint-disable-line react-hooks/exhaustive-deps, react-hooks/set-state-in-effect -- mount fetch; loader's sync setLoading/setError reset is intentional (shared with Refresh); data state is only written after the awaited request

  return (
    <div>
      <div className="mb-4">
        <SearchBar
          value={q}
          onChange={setQ}
          onSearch={() => search(0, false)}
          placeholder="Search places…"
          loading={loading}
        />
      </div>

      {error && <Banner className="mb-4">{error}</Banner>}

      <div className="overflow-x-auto rounded-lg border border-zinc-800">
        <table className="w-full text-sm">
          <thead className="bg-zinc-950 text-left text-xs uppercase tracking-wide text-zinc-500">
            <tr>
              <th className="px-3 py-2 font-medium">Name</th>
              <th className="px-3 py-2 font-medium">City</th>
              <th className="px-3 py-2 font-medium">State</th>
              <th className="px-3 py-2 font-medium">Created</th>
              <th className="px-3 py-2 font-medium"></th>
            </tr>
          </thead>
          <tbody>
            {places.map((p) => (
              <tr key={p.id} className="border-t border-zinc-800">
                <td className="px-3 py-2 text-zinc-300">
                  {String(p.props.name ?? "—")}
                </td>
                <td className="px-3 py-2 text-zinc-300">{String(p.props.city ?? "—")}</td>
                <td className="px-3 py-2 text-zinc-300">{String(p.props.state ?? "—")}</td>
                <td className="px-3 py-2 text-zinc-400 text-xs">
                  {p.created ? new Date(p.created).toLocaleDateString() : "—"}
                </td>
                <td className="px-3 py-2">
                  <Link
                    to={`/records/places/${p.id}`}
                    className="rounded bg-zinc-700 px-2 py-0.5 text-xs text-white hover:bg-zinc-600"
                  >
                    Edit
                  </Link>
                </td>
              </tr>
            ))}
            {places.length === 0 && !loading && (
              <tr>
                <td colSpan={5} className="px-3 py-4 text-center text-sm text-zinc-500">
                  No places found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {hasMore && (
        <button
          onClick={() => search(offset, true)}
          disabled={loading}
          className="mt-3 rounded bg-zinc-700 px-3 py-1.5 text-sm text-white hover:bg-zinc-600 disabled:opacity-50"
        >
          {loading ? "Loading…" : "Load more"}
        </button>
      )}
    </div>
  );
}

function EventsTab() {
  const [q, setQ] = useState("");
  const [events, setEvents] = useState<EventRecord[]>([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const search = useCallback(async (newOffset = 0, append = false) => {
    setLoading(true);
    setError(null);
    try {
      const body: Record<string, unknown> = { limit: 50, offset: newOffset };
      if (q.trim()) body["q"] = q.trim();
      const data = await invokeAdmin<{ events: EventRecord[] }>("admin_events_list", body);
      setEvents((prev) => (append ? [...prev, ...data.events] : data.events));
      setOffset(newOffset + data.events.length);
      setHasMore(data.events.length === 50);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [q]);

  useEffect(() => { search(0, false); }, []); // eslint-disable-line react-hooks/exhaustive-deps, react-hooks/set-state-in-effect -- mount fetch; loader's sync setLoading/setError reset is intentional (shared with Refresh); data state is only written after the awaited request

  return (
    <div>
      <div className="mb-4">
        <SearchBar
          value={q}
          onChange={setQ}
          onSearch={() => search(0, false)}
          placeholder="Search events…"
          loading={loading}
        />
      </div>

      {error && <Banner className="mb-4">{error}</Banner>}

      <div className="overflow-x-auto rounded-lg border border-zinc-800">
        <table className="w-full text-sm">
          <thead className="bg-zinc-950 text-left text-xs uppercase tracking-wide text-zinc-500">
            <tr>
              <th className="px-3 py-2 font-medium">Name</th>
              <th className="px-3 py-2 font-medium">Start</th>
              <th className="px-3 py-2 font-medium">Place ID</th>
              <th className="px-3 py-2 font-medium"></th>
            </tr>
          </thead>
          <tbody>
            {events.map((ev) => (
              <tr key={ev.id} className="border-t border-zinc-800">
                <td className="px-3 py-2 text-zinc-300">{String(ev.props.name ?? "—")}</td>
                <td className="px-3 py-2 text-zinc-300 text-xs">
                  {ev.props.start ? new Date(String(ev.props.start)).toLocaleString() : "—"}
                </td>
                <td className="px-3 py-2">
                  <CopyId id={ev.place_id} />
                </td>
                <td className="px-3 py-2">
                  <Link
                    to={`/records/events/${ev.id}`}
                    className="rounded bg-zinc-700 px-2 py-0.5 text-xs text-white hover:bg-zinc-600"
                  >
                    Edit
                  </Link>
                </td>
              </tr>
            ))}
            {events.length === 0 && !loading && (
              <tr>
                <td colSpan={4} className="px-3 py-4 text-center text-sm text-zinc-500">
                  No events found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {hasMore && (
        <button
          onClick={() => search(offset, true)}
          disabled={loading}
          className="mt-3 rounded bg-zinc-700 px-3 py-1.5 text-sm text-white hover:bg-zinc-600 disabled:opacity-50"
        >
          {loading ? "Loading…" : "Load more"}
        </button>
      )}
    </div>
  );
}

function CorrectionsTab() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<string>("pending");

  // Cancellation-safe page-0 load keyed on the status filter (ADM-5) — rapid
  // filter changes can't render the previous filter's rows under the new pill.
  const { data, loading, error, refetch, setData } = useAdminQuery(
    () =>
      invokeAdmin<{ corrections: CorrectionRequest[]; total?: number }>(
        "admin_corrections_list",
        { limit: 50, offset: 0, status },
      ),
    [status],
  );
  const corrections = data?.corrections ?? [];
  const total = data?.total ?? null;
  // Prefer the known total (a page whose length is an exact multiple of 50 would
  // otherwise keep hasMore=true forever and show a "Load more" that only ever
  // fetches an empty page); fall back to the page-size heuristic when total is absent.
  const hasMore =
    total !== null
      ? corrections.length < total
      : corrections.length > 0 && corrections.length % 50 === 0;

  const [loadingMore, setLoadingMore] = useState(false);
  const [moreError, setMoreError] = useState<string | null>(null);

  async function loadMore() {
    setLoadingMore(true);
    setMoreError(null);
    try {
      const more = await invokeAdmin<{ corrections: CorrectionRequest[] }>(
        "admin_corrections_list",
        { limit: 50, offset: corrections.length, status },
      );
      setData((prev) => ({
        corrections: [...(prev?.corrections ?? []), ...more.corrections],
        total: prev?.total,
      }));
    } catch (e) {
      setMoreError(errMsg(e));
    } finally {
      setLoadingMore(false);
    }
  }

  function open(id: string) {
    navigate(`/records/corrections/${id}`);
  }

  return (
    <div>
      <div className="mb-4 flex items-center gap-3">
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="rounded border border-zinc-700 bg-zinc-800 px-3 py-1.5 text-sm text-white focus:outline-none focus:ring-1 focus:ring-zinc-500"
        >
          {CORRECTION_STATUSES.map((s) => (
            <option key={s} value={s}>
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </option>
          ))}
        </select>
        {total !== null && (
          <span className="text-xs text-zinc-500">
            showing {corrections.length} of {total}
          </span>
        )}
        <button
          type="button"
          onClick={refetch}
          disabled={loading}
          className="ml-auto rounded border border-zinc-700 px-3 py-1.5 text-sm text-zinc-300 hover:bg-zinc-800 disabled:opacity-50"
        >
          {loading ? "Loading…" : "Refresh"}
        </button>
      </div>

      {error && <Banner className="mb-4">{error}</Banner>}
      {moreError && <Banner className="mb-4">{moreError}</Banner>}

      <div className="overflow-x-auto rounded-lg border border-zinc-800">
        <table className="w-full text-sm">
          <thead className="bg-zinc-950 text-left text-xs uppercase tracking-wide text-zinc-500">
            <tr>
              <th className="px-3 py-2 font-medium">Type</th>
              <th className="px-3 py-2 font-medium">Entity ID</th>
              <th className="px-3 py-2 font-medium">Reason</th>
              <th className="px-3 py-2 font-medium">Status</th>
              <th className="px-3 py-2 font-medium">Created</th>
            </tr>
          </thead>
          <tbody>
            {corrections.map((c) => (
              // Keyboard-accessible row navigation (ADM-9): focusable, Enter/Space
              // activates, visible focus ring. The Entity-ID cell's copy button
              // stops propagation so copying doesn't also navigate.
              <tr
                key={c.id}
                role="link"
                tabIndex={0}
                aria-label={`Open correction ${c.id}`}
                className="border-t border-zinc-800 cursor-pointer hover:bg-zinc-800/40 focus:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-zinc-400"
                onClick={() => open(c.id)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    open(c.id);
                  }
                }}
              >
                <td className="px-3 py-2 text-zinc-300">{c.entity_type}</td>
                <td className="px-3 py-2">
                  <CopyId id={c.entity_id} />
                </td>
                <td className="px-3 py-2 text-zinc-300 max-w-xs truncate">{c.reason}</td>
                <td className="px-3 py-2">
                  <span className={`rounded px-1.5 py-0.5 text-xs ${statusColor(c.status)}`}>
                    {c.status}
                  </span>
                </td>
                <td className="px-3 py-2 text-zinc-400 text-xs">
                  {c.created_at ? new Date(c.created_at).toLocaleDateString() : "—"}
                </td>
              </tr>
            ))}
            {corrections.length === 0 && !loading && (
              <tr>
                <td colSpan={5} className="px-3 py-4 text-center text-sm text-zinc-500">
                  No corrections found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {hasMore && (
        <button
          onClick={loadMore}
          disabled={loadingMore}
          className="mt-3 rounded bg-zinc-700 px-3 py-1.5 text-sm text-white hover:bg-zinc-600 disabled:opacity-50"
        >
          {loadingMore ? "Loading…" : "Load more"}
        </button>
      )}
    </div>
  );
}
